import React, { useEffect, useMemo, useRef } from 'react';
import { Apartment, DoneAll, Error as ErrorIcon } from '@mui/icons-material';
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
  Stack,
  styled,
  Tooltip,
  Typography
} from '@mui/material';

import {
  getAccessibleFacilityTypes,
  getCareSettingTerminologyForFacilityType,
  getCareSettingTerminologyForFacilityTypes
} from '@/hooks/facility-selection/careSettingTerminology';
import { ALL_FACILITIES, FacilityPayload } from '@/hooks/useFacilitiesQuery';
import { useIsMobileViewport } from '@/hooks/useIsMobileViewport';
import { useKeyboardNavigation } from '@/hooks/useKeyboardNavigation';
import { UserModel } from '@/models/UserModel';
import useIsScreenerAllFacilitiesSelected from '@/pages/CRM/Screener/Leads/AllLeads/hooks/useIsScreenerAllFacilitiesSelected';
import { UserFacilityAccess } from '@/types/users';

import CrmScreenerSelectAllButton from './CrmScreenerSelectAllButton';
import FacilitySearch from './FacilitySearch';

export const FacilitySelectorListItemButton = styled(ListItemButton)({
  p: '12px 16px',
  '& .MuiListItemText-primary': {
    fontSize: '14px',
    fontWeight: 500,
    color: '#0A1E28',
    lineHeight: '20px'
  },
  '& .MuiListItemText-secondary': {
    display: 'flex',
    alignItems: 'center',
    fontSize: '12px',
    fontWeight: 400,
    color: '#667A86',
    lineHeight: '16px'
  },
  '& .MuiSvgIcon-root': {
    color: '#9AAEBB'
  },
  '& .MuiListItemIcon-root': {
    minWidth: 32
  }
});

interface FacilitySelectorPopoverProps {
  selectedFacilityId: string | null;
  anchorEl: HTMLDivElement;
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  user: UserModel;
  onSelectFacility: (facilityId: string) => void;
  canSelectAllFacilities: boolean;
  isCRM: boolean;
  billingDisabledFacilityIds: Set<string>;
  anyFacilityBillingDisabled: boolean;
  facilities: FacilityPayload[];
  accessibleFacilityIds: ReadonlySet<string>;
}

const FacilitySelectorPopover: React.FC<FacilitySelectorPopoverProps> = ({
  selectedFacilityId,
  onSelectFacility,
  isOpen,
  user,
  setIsOpen,
  anchorEl,
  canSelectAllFacilities,
  isCRM,
  billingDisabledFacilityIds,
  anyFacilityBillingDisabled,
  facilities,
  accessibleFacilityIds
}) => {
  const isMobileViewport = useIsMobileViewport();
  const [inputValue, setInputValue] = React.useState('');
  const [filteredFacilities, setFilteredFacilities] = React.useState<
    UserFacilityAccess[]
  >(user.user_facility_accesses ?? []);
  const isAllFacilitiesSelected = useIsScreenerAllFacilitiesSelected();
  const rawOrganizationName = user.organization_details?.name;
  const organizationName =
    typeof rawOrganizationName === 'string' ? rawOrganizationName.trim() : '';
  const organizationNameId = React.useId();
  const shouldShowSearch = (user.user_facility_accesses?.length ?? 0) > 10;
  const careSettingTerms = useMemo(
    () =>
      getCareSettingTerminologyForFacilityTypes(
        getAccessibleFacilityTypes(facilities, accessibleFacilityIds)
      ),
    [accessibleFacilityIds, facilities]
  );
  const facilityTypeById = useMemo(
    () =>
      new Map(
        facilities.map((facility) => [facility.id, facility.facility_type])
      ),
    [facilities]
  );

  const searchInputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const { handleSearchKeyDown, handleListKeyDown } = useKeyboardNavigation({
    searchInputRef,
    listRef
  });

  useEffect(() => {
    const filteredFacilities =
      user.user_facility_accesses?.filter((facility) => {
        return facility.facility?.name
          ?.toLowerCase()
          .includes((inputValue ?? '').toLowerCase());
      }) ?? [];
    setFilteredFacilities(
      filteredFacilities.sort((a, b) => {
        return a.facility.name.localeCompare(b.facility.name);
      })
    );
  }, [inputValue, user.user_facility_accesses]);

  return (
    <Popover
      PaperProps={{
        sx: {
          maxHeight: 400,
          width: isMobileViewport ? '100%' : 265,
          boxShadow: '0px 5px 20px 2px rgba(44, 58, 66, 0.15)',
          borderRadius: '8px'
        }
      }}
      anchorEl={anchorEl}
      open={isOpen}
      onClose={() => setIsOpen(false)}
      transformOrigin={{
        vertical: 'bottom',
        horizontal: 'left'
      }}
    >
      {organizationName && (
        <Box sx={{ px: 2, pt: 2, pb: shouldShowSearch ? 0.5 : 1 }}>
          <Typography
            id={organizationNameId}
            variant="body2"
            color="text.secondary"
            lineHeight="20px"
            noWrap
            title={organizationName}
          >
            {organizationName}
          </Typography>
        </Box>
      )}
      {shouldShowSearch && (
        <FacilitySearch
          value={inputValue}
          onChange={(event) => setInputValue(event.target.value)}
          autoFocus={true}
          onKeyDown={handleSearchKeyDown}
          inputRef={searchInputRef}
        />
      )}
      <List
        dense
        sx={{ p: 0 }}
        role="listbox"
        aria-describedby={organizationName ? organizationNameId : undefined}
        ref={listRef}
        onKeyDown={handleListKeyDown}
      >
        <CrmScreenerSelectAllButton
          setIsOpen={setIsOpen}
          hide={inputValue.trim().length > 0}
          billingDisabledFacilityIds={billingDisabledFacilityIds}
          facilities={facilities}
          accessibleFacilityIds={accessibleFacilityIds}
        />
        {!isCRM && canSelectAllFacilities && !isMobileViewport && (
          <FacilitySelectorListItemButton
            role="option"
            selected={selectedFacilityId === ALL_FACILITIES}
            disabled={anyFacilityBillingDisabled}
            onClick={() => {
              onSelectFacility(ALL_FACILITIES);
              setIsOpen(false);
            }}
            sx={anyFacilityBillingDisabled ? { opacity: 0.5 } : undefined}
          >
            <ListItemIcon>
              <DoneAll />
            </ListItemIcon>
            <ListItemText>Select All</ListItemText>
            {anyFacilityBillingDisabled && (
              <Tooltip
                title={`All ${careSettingTerms.pluralTitle} view is disabled due to an unpaid balance on one or more ${careSettingTerms.pluralLower}.`}
                placement="top"
              >
                <ErrorIcon
                  sx={{
                    fontSize: 18,
                    color: '#D32F2F !important',
                    ml: 1,
                    pointerEvents: 'auto'
                  }}
                />
              </Tooltip>
            )}
          </FacilitySelectorListItemButton>
        )}
        {filteredFacilities.length > 0 ? (
          filteredFacilities.map((item) => {
            const isBillingDisabled = billingDisabledFacilityIds.has(
              item.facility_id
            );
            const itemCareSettingTerms =
              getCareSettingTerminologyForFacilityType(
                facilityTypeById.get(item.facility_id)
              );
            return (
              <FacilitySelectorListItemButton
                key={item.id}
                role="option"
                id={`community-${item.facility_id}`}
                onClick={() => onSelectFacility(item.facility_id)}
                selected={
                  isAllFacilitiesSelected
                    ? false
                    : item.facility_id === selectedFacilityId
                }
              >
                <ListItemText secondary={<>{item.facility.address}</>}>
                  {item.facility.name}
                </ListItemText>
                {isBillingDisabled && (
                  <Tooltip
                    title={`This ${itemCareSettingTerms.singularLower} has been temporarily disabled due to an outstanding balance on your account.`}
                    placement="top"
                  >
                    <ErrorIcon
                      sx={{ fontSize: 18, color: '#D32F2F !important', ml: 1 }}
                    />
                  </Tooltip>
                )}
              </FacilitySelectorListItemButton>
            );
          })
        ) : (
          <Stack height={200} justifyContent="center" alignItems="center">
            <ListItemText sx={{ textAlign: 'center' }}>
              <Stack direction="column" alignItems="center" gap={2}>
                <Box
                  bgcolor="#B5C9D6"
                  borderRadius="50%"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  p={1}
                  height={50}
                  width={50}
                >
                  <Apartment sx={{ color: '#FFFFFF !important' }} />
                </Box>
                <Typography fontSize="16px" fontWeight={500} color="#667A86">
                  No {careSettingTerms.pluralTitle} Found
                </Typography>
              </Stack>
            </ListItemText>
          </Stack>
        )}
      </List>
    </Popover>
  );
};

export default FacilitySelectorPopover;
